"use client";

import { WarningCircle } from "@phosphor-icons/react";
import type { ComponentProps, ReactNode } from "react";
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { TProps as JsxParserProps } from "react-jsx-parser";
import JsxParser from "react-jsx-parser";
import { Icon } from "@/lib/icon";
import { cn } from "@/lib/utils";

interface JSXPreviewContextValue {
  jsx: string;
  processedJsx: string;
  /**
   * Byte offset in `processedJsx` where the auto-closed tag soup begins.
   * Equals `processedJsx.length` when nothing was auto-closed (non-
   * streaming, or streamed input that was already well-formed). Used by
   * `JSXPreviewContent` to inject the streaming cursor at the exact
   * boundary between live content and the auto-closed wrapper tags so
   * the cursor lands inside the deepest still-open element instead of
   * after the outermost wrapper.
   */
  processedJsxCloseStart: number;
  isStreaming: boolean;
  error: Error | null;
  setError: (error: Error | null) => void;
  setLastGoodJsx: (jsx: string) => void;
  components: JsxParserProps["components"];
  bindings: JsxParserProps["bindings"];
  onErrorProp?: (error: Error) => void;
}

const JSXPreviewContext = createContext<JSXPreviewContextValue | null>(null);

const TAG_REGEX = /<\/?([a-zA-Z][a-zA-Z0-9]*)\s*([^>]*?)(\/)?>/;

export const useJSXPreview = () => {
  const context = useContext(JSXPreviewContext);
  if (!context) {
    throw new Error("JSXPreview components must be used within JSXPreview");
  }
  return context;
};

const matchJsxTag = (code: string) => {
  if (code.trim() === "") {
    return null;
  }

  const match = code.match(TAG_REGEX);

  if (!match || match.index === undefined) {
    return null;
  }

  const [fullMatch, tagName, attributes, selfClosing] = match;

  let type: "self-closing" | "closing" | "opening";
  if (selfClosing) {
    type = "self-closing";
  } else if (fullMatch.startsWith("</")) {
    type = "closing";
  } else {
    type = "opening";
  }

  return {
    attributes: attributes.trim(),
    endIndex: match.index + fullMatch.length,
    startIndex: match.index,
    tag: fullMatch,
    tagName,
    type,
  };
};

const stripIncompleteTag = (text: string) => {
  // Find the last '<' that isn't part of a complete tag
  const lastOpen = text.lastIndexOf("<");
  if (lastOpen === -1) {
    return text;
  }

  const afterOpen = text.slice(lastOpen);
  // If there's no closing '>' after the last '<', it's an incomplete tag
  if (!afterOpen.includes(">")) {
    return text.slice(0, lastOpen);
  }

  return text;
};

const completeJsxTag = (code: string) => {
  const stack: string[] = [];
  let result = "";
  let currentPosition = 0;

  while (currentPosition < code.length) {
    const match = matchJsxTag(code.slice(currentPosition));
    if (!match) {
      // No more tags found, strip any trailing incomplete tag
      result += stripIncompleteTag(code.slice(currentPosition));
      break;
    }
    const { tagName, type, endIndex } = match;

    // Include any text content before this tag
    result += code.slice(currentPosition, currentPosition + endIndex);

    if (type === "opening") {
      stack.push(tagName);
    } else if (type === "closing") {
      stack.pop();
    }

    currentPosition += endIndex;
  }

  // `closeStart` is the boundary between the consumer's streamed content
  // (`result`) and the auto-closed tag soup we append. Callers that want
  // to insert a marker at the deepest still-open element use this offset
  // — it's the only place inside the produced JSX that's "inside every
  // currently-open element" simultaneously.
  const closing = stack
    .toReversed()
    .map((tag) => `</${tag}>`)
    .join("");

  return {
    jsx: result + closing,
    closeStart: result.length,
  };
};

export type JSXPreviewProps = ComponentProps<"div"> & {
  jsx: string;
  isStreaming?: boolean;
  components?: JsxParserProps["components"];
  bindings?: JsxParserProps["bindings"];
  onError?: (error: Error) => void;
};

export const JSXPreview = memo(
  ({
    jsx,
    isStreaming = false,
    components,
    bindings,
    onError,
    className,
    children,
    ...props
  }: JSXPreviewProps) => {
    const [prevJsx, setPrevJsx] = useState(jsx);
    const [error, setError] = useState<Error | null>(null);
    const [_lastGoodJsx, setLastGoodJsx] = useState("");

    // Clear error when jsx changes (derived state pattern)
    if (jsx !== prevJsx) {
      setPrevJsx(jsx);
      setError(null);
    }

    const { processedJsx, processedJsxCloseStart } = useMemo(() => {
      if (isStreaming) {
        const { jsx: completed, closeStart } = completeJsxTag(jsx);
        return {
          processedJsx: completed,
          processedJsxCloseStart: closeStart,
        };
      }
      return { processedJsx: jsx, processedJsxCloseStart: jsx.length };
    }, [jsx, isStreaming]);

    const contextValue = useMemo(
      () => ({
        bindings,
        components,
        error,
        isStreaming,
        jsx,
        onErrorProp: onError,
        processedJsx,
        processedJsxCloseStart,
        setError,
        setLastGoodJsx,
      }),
      // useState setters are referentially stable in React and don't
      // need to be listed (Biome exhaustive-deps flags them as extra).
      [
        bindings,
        components,
        error,
        isStreaming,
        jsx,
        onError,
        processedJsx,
        processedJsxCloseStart,
      ],
    );

    return (
      <JSXPreviewContext.Provider value={contextValue}>
        <div className={cn("relative", className)} {...props}>
          {children}
        </div>
      </JSXPreviewContext.Provider>
    );
  },
);

JSXPreview.displayName = "JSXPreview";

// Streaming cursor — injected into the parsed JSX (not a sibling of the
// parser output) so it renders inline at the end of the last text node
// instead of dropping to a new line below a block-level final element.
const StreamingCursor = () => (
  <span
    aria-hidden
    className="inline-block h-4 w-2 animate-pulse bg-foreground align-middle"
  />
);

// Inject <StreamingCursor /> at the exact boundary between the
// consumer's streamed content and the auto-closed tag soup that
// `completeJsxTag` appends. That offset is the only place in the
// produced JSX that's inside every currently-open element at once —
// putting the cursor there makes it land inline with the last text
// node of the deepest open element (e.g. inside a streaming `<p>`)
// instead of as a sibling of that block, which is what happened when
// the previous implementation used `lastIndexOf("</")` and landed on
// the OUTERMOST closing tag.
//
// `closeStart >= jsx.length` covers the case where nothing was auto-
// closed (e.g. consumer streamed already-well-formed JSX, or the
// fallback path uses the last-good JSX whose closeStart we didn't
// track) — append at the end.
const injectStreamingCursor = (jsx: string, closeStart: number) => {
  if (closeStart >= jsx.length) {
    return `${jsx}<StreamingCursor />`;
  }
  return `${jsx.slice(0, closeStart)}<StreamingCursor />${jsx.slice(closeStart)}`;
};

export type JSXPreviewContentProps = Omit<ComponentProps<"div">, "children">;

export const JSXPreviewContent = memo(
  ({ className, ...props }: JSXPreviewContentProps) => {
    const {
      processedJsx,
      processedJsxCloseStart,
      isStreaming,
      components,
      bindings,
      setError,
      setLastGoodJsx,
      onErrorProp,
    } = useJSXPreview();
    const errorReportedRef = useRef<string | null>(null);
    const lastGoodJsxRef = useRef("");
    const [hadError, setHadError] = useState(false);

    // Reset error tracking when jsx changes
    useEffect(() => {
      errorReportedRef.current = null;
      setHadError(false);
    }, [processedJsx]);

    const handleError = useCallback(
      (err: Error) => {
        // Prevent duplicate error reports for the same jsx
        if (errorReportedRef.current === processedJsx) {
          return;
        }
        errorReportedRef.current = processedJsx;

        // During streaming, suppress errors and fall back to last good JSX
        if (isStreaming) {
          setHadError(true);
          return;
        }

        setError(err);
        onErrorProp?.(err);
      },
      [processedJsx, isStreaming, onErrorProp, setError],
    );

    // Track the last JSX that rendered without error
    useEffect(() => {
      if (!errorReportedRef.current) {
        lastGoodJsxRef.current = processedJsx;
        setLastGoodJsx(processedJsx);
      }
    }, [processedJsx, setLastGoodJsx]);

    // During streaming, if the current JSX errored, re-render with last good version
    const displayJsx =
      isStreaming && hadError ? lastGoodJsxRef.current : processedJsx;

    // Inject the cursor into the JSX itself when streaming — keeps it
    // inline with the last text node instead of dropping below a block
    // element. Register the cursor as a known component for the parser.
    //
    // When we're using `displayJsx === processedJsx`, the offset from
    // context is the precise streaming boundary. On the error fallback
    // path (`displayJsx === lastGoodJsxRef.current`) we don't have a
    // boundary for the fallback string, so passing the current
    // processedJsx's offset would land the cursor at the wrong index;
    // `injectStreamingCursor` falls back to appending at the end when
    // `closeStart >= jsx.length`, which is the right behaviour for the
    // rare fallback case.
    const usingFallback = displayJsx !== processedJsx;
    const finalJsx = isStreaming
      ? injectStreamingCursor(
          displayJsx,
          usingFallback ? displayJsx.length : processedJsxCloseStart,
        )
      : displayJsx;
    const enhancedComponents = useMemo(
      () => (isStreaming ? { ...components, StreamingCursor } : components),
      [components, isStreaming],
    );

    return (
      <div
        className={cn(
          "jsx-preview-content flex flex-col gap-2 rounded-md border border-border bg-card p-4 text-card-foreground",
          className,
        )}
        {...props}
      >
        <JsxParser
          bindings={bindings}
          components={enhancedComponents}
          jsx={finalJsx}
          onError={handleError}
          renderInWrapper={false}
        />
      </div>
    );
  },
);

JSXPreviewContent.displayName = "JSXPreviewContent";

export type JSXPreviewErrorProps = ComponentProps<"div"> & {
  children?: ReactNode | ((error: Error) => ReactNode);
};

const renderChildren = (
  children: ReactNode | ((error: Error) => ReactNode),
  error: Error,
): ReactNode => {
  if (typeof children === "function") {
    return children(error);
  }
  return children;
};

export const JSXPreviewError = memo(
  ({ className, children, ...props }: JSXPreviewErrorProps) => {
    const { error } = useJSXPreview();

    if (!error) {
      return null;
    }

    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-destructive text-sm",
          className,
        )}
        {...props}
      >
        {children ? (
          renderChildren(children, error)
        ) : (
          <>
            <Icon className="size-4 shrink-0" icon={WarningCircle} />
            <span>{error.message}</span>
          </>
        )}
      </div>
    );
  },
);

JSXPreviewError.displayName = "JSXPreviewError";
